import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { AlertTriangle, Mail, Copy, CheckCircle2 } from "lucide-react";

const SUPPORT_EMAIL = "info@dasabo.com";

/**
 * Editorial error dialog for SDI transmission failures.
 *
 * Design principles:
 *  - Never mention upstream providers (Autofattura is the single point of
 *    contact for the customer). The backend already sanitizes messages;
 *    this component just presents them with a coherent visual language.
 *  - Actionable: prominent CTA to write to support with a pre-composed
 *    subject line + one-click "copy invoice number" affordance.
 *  - Editorial: matches the rest of the app (serif accent, editorial
 *    tone/margins, sharp corners, light warm palette).
 */
export default function SdiErrorDialog({
  open,
  onOpenChange,
  title = "Trasmissione al SDI non riuscita",
  message,
  invoiceRef,
  errors, // optional list of {id, error} for bulk operations
  testid = "sdi-error-dialog",
}) {
  const [copied, setCopied] = React.useState(false);

  const subject = invoiceRef
    ? `Problema trasmissione SDI · ${invoiceRef}`
    : "Problema trasmissione SDI";
  const body = [
    "Ciao Autofattura,",
    "",
    "sto riscontrando un problema durante la trasmissione al Sistema di Interscambio.",
    invoiceRef ? `Riferimento fattura: ${invoiceRef}` : null,
    message ? `Messaggio ricevuto: ${message}` : null,
    "",
    "Grazie.",
  ]
    .filter(Boolean)
    .join("\n");
  const mailto = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
    subject,
  )}&body=${encodeURIComponent(body)}`;

  const copyRef = () => {
    if (!invoiceRef) return;
    navigator.clipboard.writeText(invoiceRef);
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  const bulkErrors = Array.isArray(errors) ? errors : [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[480px] rounded-2xl border-[#E8E4D9] p-0 overflow-hidden"
        data-testid={testid}
      >
        {/* Warm banner strip */}
        <div className="bg-gradient-to-br from-red-50 via-orange-50 to-amber-50 px-6 pt-6 pb-5 border-b border-[#E8E4D9]">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-xl bg-red-100 text-red-600 grid place-items-center shrink-0 border border-red-200 shadow-sm">
              <AlertTriangle className="w-5 h-5" strokeWidth={2.4} />
            </div>
            <DialogHeader className="space-y-1 text-left">
              <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-red-700 font-bold">
                <span className="w-4 h-px bg-red-700" />
                Errore
              </div>
              <DialogTitle
                className="font-display font-extrabold text-2xl tracking-tighter text-[#0A0A0A] leading-tight"
                data-testid="sdi-error-title"
              >
                {title}
              </DialogTitle>
            </DialogHeader>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">
          {message && (
            <DialogDescription
              className="text-[15px] leading-relaxed text-[#4A4A4A]"
              data-testid="sdi-error-message"
            >
              {message}
            </DialogDescription>
          )}

          {bulkErrors.length > 0 && (
            <div
              className="rounded-lg border border-[#E8E4D9] bg-[#FAF9F6] p-3 max-h-40 overflow-y-auto"
              data-testid="sdi-error-bulk-list"
            >
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#737373] mb-2">
                Dettaglio errori · {bulkErrors.length} fattur{bulkErrors.length === 1 ? "a" : "e"}
              </div>
              <ul className="space-y-1.5">
                {bulkErrors.map((e, i) => (
                  <li key={e.id || `err-${i}`} className="text-xs text-[#4A4A4A] flex items-start gap-2">
                    <span className="font-mono text-[#737373] shrink-0">
                      {(e.id || "").slice(0, 8)}
                    </span>
                    <span>·</span>
                    <span className="flex-1">{e.error || "errore sconosciuto"}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {invoiceRef && (
            <div className="flex items-center gap-2 rounded-lg border border-dashed border-[#E8E4D9] bg-white px-3 py-2.5">
              <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#737373]">
                Rif.
              </span>
              <span
                className="font-mono text-sm font-semibold text-[#0A0A0A] flex-1 truncate"
                data-testid="sdi-error-invoice-ref"
              >
                {invoiceRef}
              </span>
              <button
                type="button"
                onClick={copyRef}
                data-testid="sdi-error-copy-ref"
                className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-semibold text-[#1D4ED8] hover:bg-[#F5F2EA] transition-colors"
              >
                {copied ? (
                  <>
                    <CheckCircle2 className="w-3 h-3" /> Copiato
                  </>
                ) : (
                  <>
                    <Copy className="w-3 h-3" /> Copia
                  </>
                )}
              </button>
            </div>
          )}

          {/* Support callout */}
          <div className="rounded-xl border border-[#E8E4D9] bg-[#F5F2EA] p-4">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-lg bg-white border border-[#E8E4D9] grid place-items-center shrink-0">
                <Mail className="w-4 h-4 text-[#1D4ED8]" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-[#0A0A0A]">
                  Il nostro <span className="font-serif-accent italic text-[#1D4ED8]">supporto</span> è qui per te
                </div>
                <p className="text-xs text-[#4A4A4A] mt-0.5 leading-relaxed">
                  Scrivici e risolviamo il problema per conto tuo. Rispondiamo entro poche ore lavorative.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <DialogFooter className="px-6 pb-6 pt-2 flex-col-reverse sm:flex-row gap-2 sm:gap-2 sm:justify-end">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            data-testid="sdi-error-close"
            className="w-full sm:w-auto px-4 py-2.5 rounded-md border border-[#E8E4D9] bg-white text-sm font-medium text-[#4A4A4A] hover:bg-[#F5F2EA] transition-colors"
          >
            Chiudi
          </button>
          <a
            href={mailto}
            data-testid="sdi-error-contact-support"
            className="w-full sm:w-auto inline-flex items-center justify-center gap-1.5 px-5 py-2.5 rounded-md bg-[#0A0A0A] text-white text-sm font-semibold hover:bg-[#1D4ED8] transition-colors"
          >
            <Mail className="w-4 h-4" />
            Contatta il supporto
          </a>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
